import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Users, Building2, FileText, Clock, AlertCircle, Receipt } from 'lucide-react';
import { fmtDateWeek } from '../lib/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, subtitle, icon: Icon, bgColor, iconColor, onClick, dataTestId }) => (
  <Card 
    className={`bg-white border-0 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    onClick={onClick}
    data-testid={dataTestId}
  >
    <CardContent className="p-3.5 sm:p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm font-medium text-slate-600 leading-tight">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight truncate">{value}</p>
          {subtitle && <p className="text-[11px] sm:text-xs text-slate-500 mt-1 leading-tight">{subtitle}</p>}
        </div>
        <div className={`w-9 h-9 sm:w-12 sm:h-12 shrink-0 ${bgColor} rounded-full flex items-center justify-center`}>
          <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${iconColor}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const fmtCurrency = (amount, currency = 'PLN') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(amount);
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return fmtDateWeek(d) || d; } catch { return d; }
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  partially_paid: 'bg-yellow-100 text-yellow-800',
};

const DashboardStats = ({ employees, projects, customers, timesheets, onNavigate }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  // Total hours come from a server-side aggregation — summing the
  // fetched timesheets client-side stalls once the list hits the API's
  // row cap.
  const [hoursTotal, setHoursTotal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/employee-invoices/summary`)
      .then(r => setSummary(r.data))
      .catch(() => {});
    axios.get(`${API}/api/timesheets/summary`)
      .then(r => setHoursTotal(r.data?.total_hours ?? null))
      .catch(() => {});
  }, []);

  const totalHours = hoursTotal != null
    ? hoursTotal
    : timesheets.reduce((acc, ts) => acc + (ts.hours || 0), 0);

  const outstandingLabel = summary
    ? Object.entries(summary.total_outstanding || {}).map(([cur, amt]) => fmtCurrency(amt, cur)).join(' + ') || '0'
    : '...';

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6" data-testid="dashboard-stat-grid">
        <StatCard
          title={t('dashboard.totalEmployees')}
          value={employees.length}
          icon={Users}
          bgColor="bg-blue-100"
          iconColor="text-blue-600"
          onClick={onNavigate ? () => onNavigate('employees') : undefined}
          dataTestId="stat-employees"
        />
        <StatCard
          title={t('dashboard.activeProjects')}
          value={projects.length}
          icon={Building2}
          bgColor="bg-green-100"
          iconColor="text-green-600"
          onClick={onNavigate ? () => onNavigate('projects') : undefined}
          dataTestId="stat-projects"
        />
        <StatCard
          title={t('dashboard.customers')}
          value={customers.length}
          icon={FileText}
          bgColor="bg-purple-100"
          iconColor="text-purple-600"
          onClick={onNavigate ? () => onNavigate('customers') : undefined}
          dataTestId="stat-customers"
        />
        <StatCard
          title={t('dashboard.totalHours')}
          value={totalHours}
          icon={Clock}
          bgColor="bg-orange-100"
          iconColor="text-orange-600"
          onClick={onNavigate ? () => onNavigate('weekly-timesheets') : undefined}
          dataTestId="stat-hours"
        />
        <StatCard
          title={t('dashboard.unpaidInvoices')}
          value={summary ? summary.unpaid_count : '...'}
          subtitle={summary?.overdue_count ? t('dashboard.overdueCount', { count: summary.overdue_count }) : undefined}
          icon={Receipt}
          bgColor={summary?.overdue_count ? 'bg-red-100' : 'bg-amber-100'}
          iconColor={summary?.overdue_count ? 'text-red-600' : 'text-amber-600'}
          onClick={onNavigate ? () => onNavigate('invoices') : undefined}
          dataTestId="stat-unpaid-invoices"
        />
      </div>

      {summary && summary.unpaid_count > 0 && (
        <Card className="bg-white border-0 shadow-sm" data-testid="unpaid-invoices-summary">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base sm:text-lg font-semibold text-slate-800">{t('dashboard.outstandingInvoices')}</CardTitle>
              <div className="flex items-center gap-3 flex-wrap">
                {Object.entries(summary.total_outstanding || {}).map(([cur, amt]) => (
                  <span key={cur} className="text-sm font-bold text-slate-700" data-testid={`outstanding-${cur.toLowerCase()}`}>
                    {fmtCurrency(amt, cur)}
                  </span>
                ))}
                {summary.overdue_count > 0 && (
                  <Badge className="bg-red-100 text-red-700" data-testid="overdue-badge">
                    <AlertCircle className="w-3 h-3 mr-1" /> {t('dashboard.overdueCount', { count: summary.overdue_count })}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="min-w-0">
            {/* Mobile: one stacked card per invoice — a 5-column table is
                unreadable at 390px even with a scroller. */}
            <ul className="space-y-2 sm:hidden" data-testid="unpaid-invoices-mobile-list">
              {(summary.recent_unpaid || []).map(inv => {
                const isOverdue = inv.status !== 'draft' && new Date(inv.due_date) < new Date();
                return (
                  <li
                    key={inv.id}
                    className={`rounded-lg border p-3 ${isOverdue ? 'border-red-200 bg-red-50/50' : 'border-slate-200'}`}
                    onClick={onNavigate ? () => onNavigate('invoices') : undefined}
                    data-testid={`unpaid-card-${inv.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{inv.invoice_number}</p>
                        <p className="text-xs text-slate-600 truncate">{inv.employee_name || '-'}</p>
                      </div>
                      <p className="text-sm font-bold text-slate-900 shrink-0">
                        {fmtCurrency(inv.amount_remaining, inv.currency)}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                        {t('dashboard.dueDate')}: {fmtDate(inv.due_date)}
                        {isOverdue && <AlertCircle className="w-3 h-3 inline ml-1 -mt-0.5" />}
                      </span>
                      <Badge className={`${statusColors[inv.status] || 'bg-gray-100 text-gray-800'} text-[10px]`}>
                        {t(`dashboard.invoiceStatus.${inv.status}`, { defaultValue: inv.status })}
                      </Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="hidden sm:block w-full max-w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('dashboard.invoiceNumber')}</TableHead>
                  <TableHead>{t('dashboard.employee')}</TableHead>
                  <TableHead>{t('dashboard.dueDate')}</TableHead>
                  <TableHead className="text-right">{t('dashboard.outstanding')}</TableHead>
                  <TableHead>{t('dashboard.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(summary.recent_unpaid || []).map(inv => {
                  const isOverdue = inv.status !== 'draft' && new Date(inv.due_date) < new Date();
                  return (
                    <TableRow
                      key={inv.id}
                      className={`cursor-pointer hover:bg-slate-50 ${isOverdue ? 'bg-red-50/50' : ''}`}
                      onClick={onNavigate ? () => onNavigate('invoices') : undefined}
                      data-testid={`unpaid-row-${inv.id}`}
                    >
                      <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                      <TableCell>{inv.employee_name || '-'}</TableCell>
                      <TableCell>
                        <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                          {fmtDate(inv.due_date)}
                          {isOverdue && <AlertCircle className="w-3.5 h-3.5 inline ml-1 -mt-0.5" />}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {fmtCurrency(inv.amount_remaining, inv.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[inv.status] || 'bg-gray-100 text-gray-800'}>
                          {t(`dashboard.invoiceStatus.${inv.status}`, { defaultValue: inv.status })}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardStats;
